import { Fragment, useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  CardActions,
  Button,
  Modal,
  Backdrop,
  Fade,
  CircularProgress
} from "@material-ui/core";
import { KeyboardBackspace, Close } from '@material-ui/icons';
import { CONSTANTS } from '../../constants/app-constant';
import ReactMarkdown from 'react-markdown';
import { getBackendAttribution } from '../../utils/attribution';
import { getExperimentPayload, trackExperimentExposure } from '../../utils/experiments';
import { api } from "../../services/apiService";
import "./subscriptions-list.scss";

// The render below sorts the list with this same comparator; keeping them
// identical is what lets the experiment preselect address plans by index.
const byPlanName = (a, b) => a.name.localeCompare(b.name, undefined, {
  numeric: true,
  sensitivity: 'base'
});

// apiService's doGetCall NEVER rejects: a network failure RESOLVES with the
// Error object, and a non-200 RESOLVES with {status, errMsg}. So eligibility
// must be classified from the RESOLVED shape — a try/catch around the call is
// dead code. Only a well-formed boolean payload is a definitive answer;
// anything else (Error instance, {status,…}/{…errMsg} error shape, or a
// malformed body) is UNKNOWN.
export const classifyTrialEligibility = (res) => {
  if (res === true || res === false) {
    return res;
  }
  return null; // unknown
};

export default function SubscriptionsListView(props) {
  const { packageList, userData, onConfirmation, onPaymentLoadSuccess, showPlanDetailsModal, hidePlanDetailsModal, isActivePlan, retryPlanId, onRetryConsumed } = props;
  const [selectedSub, setSelectedSub] = useState(-1);
  const [selectedPackage, setSelectedPackage] = useState({});
  // const [selectedSubType, setSelectedSubType] = useState('BASIC');
  const [showPaymentBtn, setShowPaymentBtn] = useState(false);
  const [processType, setProcessType] = useState('sub-list');
  const [couponList, setCouponList] = useState([]);
  const [couponModal, setCouponModal] = useState(false);
  const [planDetailsModal, setPlanDetailsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState({});
  const [appliedCoupon, setAppliedCoupon] = useState({});
  const [planDetails, setPlanDetails] = useState({});
  const [trialEligible, setTrialEligible] = useState(false);
  // const history = useHistory();

  const onSubscriptionSelect = (evt, item, index) => {
    const clickedOnShowDetailsBtn = evt.target.className?.includes('show-plan-details');

    if (clickedOnShowDetailsBtn) {
      return;
    }

    setSelectedSub(index);
    setSelectedPackage(item);

    if (!showPaymentBtn) {
      setShowPaymentBtn(true);
    }
  };

  /*
   * pricing-pro-annual-default experiment: purely config-driven. The payload's
   * defaultSelectedPlanDurationDays picks which plan (if any) starts selected;
   * the control payload is null = today's no-preselection behavior, so with the
   * experiment flag off (or dark) this effect changes nothing. Reaching the plan
   * list IS the exposure (control users see the default too), so the emit sits
   * here — it no-ops unless the experiment is live, and dedups per unit.
   */
  useEffect(() => {
    // A B3 retry preselection owns the selection; don't let the experiment
    // default fight it.
    if (!packageList?.length || selectedSub !== -1 || retryPlanId) {
      return;
    }
    trackExperimentExposure('pricing-pro-annual-default', userData?.id);
    const { defaultSelectedPlanDurationDays } = getExperimentPayload('pricing-pro-annual-default', userData?.id);
    if (defaultSelectedPlanDurationDays === null || defaultSelectedPlanDurationDays === undefined) {
      return;
    }
    const sorted = [...packageList].sort(byPlanName);
    const defaultIndex = sorted.findIndex(
      (item) => item.price !== 0 && item.durationInDay === defaultSelectedPlanDurationDays
    );
    if (defaultIndex !== -1) {
      setSelectedSub(defaultIndex);
      setSelectedPackage(sorted[defaultIndex]);
      setShowPaymentBtn(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageList, userData?.id]);

  const onCheckout = () => {
    setProcessType('checkout');
    setLoading(false);
    window.scrollTo(0, 0);
  };

  const hideCheckout = () => {
    setProcessType('sub-list');
    setLoading(false);
  };

  const handleCouponModal = (open) => {
    setCouponModal(open);

    if (open) {
      setSelectedCoupon({});
    }
  };

  useEffect(() => {
    setLoading(onPaymentLoadSuccess);
  }, [onPaymentLoadSuccess]);

  useEffect(() => {
    if (showPlanDetailsModal) {
      setPlanDetails(showPlanDetailsModal);
      setPlanDetailsModal(true);
    }
  }, [showPlanDetailsModal]);

  useEffect(() => {
    // Trial copy stays hidden until the backend confirms this user never held
    // a paid plan — the server enforces the same rule at checkout.
    //
    // B2: distinguish "not eligible" (a definitive boolean `false` payload)
    // from "unknown" (network Error object, {status, errMsg} shape, malformed
    // body — doGetCall resolves ALL of these, it never rejects; see
    // classifyTrialEligibility). A transient hiccup must NOT silently degrade
    // an eligible user to the full-price wall: on unknown, retry once; if
    // still unknown, render the trial copy optimistically. The server
    // re-checks eligibility at checkout (resolveTrialPeriodDays) and the
    // Stripe checkout page shows the true amount due before card entry, so an
    // optimistic false-positive is corrected there rather than costing a
    // conversion up front.
    if (!userData?.id) {
      return undefined;
    }

    let cancelled = false;

    const fetchEligibility = () =>
      api.getTrialEligibility(userData.id)
        .then(classifyTrialEligibility)
        // Defensive only — the current apiService contract cannot reject.
        .catch(() => null);

    const resolveEligibility = async () => {
      let eligible = await fetchEligibility();
      if (eligible === null) {
        eligible = await fetchEligibility(); // exactly one retry
      }
      if (!cancelled) {
        // Still unknown after the retry → optimistic.
        setTrialEligible(eligible === null ? true : eligible);
      }
    };

    resolveEligibility();

    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  // B3 recovery: a failed checkout round-trips through the route with the plan
  // the user was buying (see subscriptions.jsx closeStatus). Re-select it and
  // reopen the SAME checkout so "Try Again" retries the same plan instead of
  // dumping the user on an empty plan list.
  //
  // The retry state is consumed EXACTLY ONCE: the ref guard makes parent
  // re-renders inert (packageList is a fresh array identity on every parent
  // render — without the guard, any redux settle would force-reopen a checkout
  // the user backed out of), and onRetryConsumed lets the parent strip the
  // route state (history.replace) so refresh/back-navigation can't replay the
  // forced-open checkout.
  const retryConsumedRef = useRef(false);
  useEffect(() => {
    if (retryConsumedRef.current || !retryPlanId || !packageList?.length) {
      return;
    }
    retryConsumedRef.current = true;
    const sorted = [...packageList].sort(byPlanName);
    const retryIndex = sorted.findIndex((item) => item.id === retryPlanId);
    if (retryIndex !== -1) {
      setSelectedSub(retryIndex);
      setSelectedPackage(sorted[retryIndex]);
      setShowPaymentBtn(true);
      setProcessType('checkout');
    }
    // Consume even when the plan is gone from the list (stale id): the state
    // must still be stripped so it can't replay.
    if (onRetryConsumed) {
      onRetryConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryPlanId, packageList]);


  const onCouponSelect = (item) => {
    setSelectedCoupon(item);
  };

  const onCouponApplied = () => {
    setAppliedCoupon(selectedCoupon);
    setCouponModal(false);
  };

  const getDiscountedPrice = (price) => {
    const priceAmt = parseFloat(price);

    if (appliedCoupon.discountType === 'percentage') {
      return priceAmt - (appliedCoupon.discountPercentage / 100 * priceAmt);
    } else {
      return priceAmt - appliedCoupon.discountAmount;
    }
  };

  const getSelectedPrice = () => {
    return selectedCoupon.couponCode ? getDiscountedPrice(selectedPackage.price) : selectedPackage.price;
  };

  const hasTrial = (plan) => {
    return trialEligible && plan?.trialPeriodDays > 0;
  };

  const getBillingCadence = (plan) => {
    const durationInDay = parseInt(plan?.durationInDay, 10);

    if (Number.isNaN(durationInDay)) {
      return '';
    }

    if (durationInDay >= 28 && durationInDay <= 31) {
      return '/month';
    }

    if (durationInDay >= 365 && durationInDay <= 366) {
      return '/year';
    }

    return ` every ${durationInDay} days`;
  };

  const onCheckoutConfirmation = () => {
    setLoading(true);
    const attribution = getBackendAttribution();
    const payload = {
      planId: selectedPackage.id,
      userId: userData.id
    };

    if (attribution) {
      payload.attribution = attribution;
    }

    onConfirmation(payload);
  };

  const showPlanDetails = (plan) => {
    setPlanDetails(plan);
    setPlanDetailsModal(true);
  };

  const hidePlanModal = () => {
    setPlanDetailsModal(false);
    hidePlanDetailsModal();
  };

  return (
    <Box className="sub-wrapper">
      {processType === 'sub-list' &&
        <>
          <Typography variant="h6" className="plan-header">Choose your plan</Typography>
          <Card className="subscription-card">
            <CardContent>
              <Typography variant="h6" className="new-plan-header">
                Available QLearn Plans
              </Typography>

              {packageList?.length > 0 &&
                <>
                  <List component="nav" className="sub-list">
                    {[...packageList].sort(byPlanName).map((item, index) => {
                      return <Fragment key={index}>
                        {item.price !== 0 &&
                          <Fragment>
                            <ListItem button
                              selected={selectedSub === index}
                              onClick={(evt) => onSubscriptionSelect(evt, item, index)}>
                              <ListItemText primary={item.name}
                                secondary={<>
                                  {hasTrial(item) &&
                                    <>
                                      <Typography variant="caption" component="span" display="block" className="trial-offer-text">
                                        {item.trialPeriodDays} days free, then ${item.price}{getBillingCadence(item)}
                                      </Typography>
                                      <Typography variant="caption" component="span" display="block" className="trial-cancel-text">
                                        Cancel anytime before your trial ends — you won't be charged.
                                      </Typography>
                                    </>
                                  }
                                  <Typography variant="caption">
                                    Daily Question Limit: {item.questionsPerDay}
                                  </Typography>, &nbsp;
                                  <Typography variant="caption">
                                    Duration: {CONSTANTS.durationObj[item.durationInDay] || item.durationInDay}
                                  </Typography>
                                  <Typography variant="caption" className="show-plan-details"
                                    onClick={() => showPlanDetails(item)}>Show Details</Typography>
                                </>}
                              />
                              <Box className="price-cont">
                                {!appliedCoupon.couponCode &&
                                  <Typography className="price-text">${item.price}<span className="price-cadence">{getBillingCadence(item)}</span></Typography>
                                }
                                {appliedCoupon.couponCode &&
                                  <>
                                    <Typography className="new-price-text">${getDiscountedPrice(item.price)} </Typography>
                                    <Typography className="price-text deselected">${item.price} </Typography>
                                  </>
                                }
                              </Box>
                            </ListItem>

                            <Divider />
                          </Fragment>
                        }
                      </Fragment>
                    })}
                  </List>
                  <Typography color="textSecondary" variant="caption" display="block">
                    Your plan will activate upon purchase. You can purchase and switch between other plans at any time.
                  </Typography>
                </>
              }

              {packageList?.length === 0 &&
                <Typography color="textSecondary" variant="caption" display="block">
                  No plans found.
                </Typography>
              }

            </CardContent>

            {showPaymentBtn &&
              <CardActions>
                {/* <Button color="primary" onClick={() => handleCouponModal(true)}>
                  Apply Coupon
                </Button> */}
                <Box className="checkout-btn-cont">
                  <Button variant="contained" color="primary" onClick={onCheckout} disabled={isActivePlan}>
                    {hasTrial(selectedPackage) ? 'Start free trial' : 'Proceed to Checkout'}
                  </Button>
                  {isActivePlan && <Typography variant="caption">You already have an active plan.</Typography>}
                </Box>

              </CardActions>
            }
          </Card>
        </>
      }
      {processType === 'checkout' &&
        <Box className="checkout-wrapper">
          <Box className="checkout-header">
            <KeyboardBackspace onClick={hideCheckout} color="primary" />
            <Box className="checkout-header-cont">
              <Typography className="bold">You're almost done!</Typography>
              <Typography variant="caption">Make sure the below information is correct before you proceed</Typography>
            </Box>
          </Box>
          <Box className="checkout-details">
            <Box className="checkout-details-items">
              <Typography>Package: <b>{selectedPackage.name}</b></Typography>
              {hasTrial(selectedPackage) ? (
                <>
                  <Typography>Due today: <b>$0.00</b></Typography>
                  <Typography>After {selectedPackage.trialPeriodDays}-day trial: <b>${getSelectedPrice()}{getBillingCadence(selectedPackage)}</b></Typography>
                </>
              ) : (
                <Typography>Price: <b>${getSelectedPrice()}</b></Typography>
              )}
              <Typography>Email: <b>{userData.email}</b></Typography>
            </Box>
            {hasTrial(selectedPackage) &&
              <Typography className="trial-disclosure">
                {selectedPackage.trialPeriodDays} days free, then ${getSelectedPrice()}{getBillingCadence(selectedPackage)}. Cancel anytime.
              </Typography>
            }
            <Button variant="contained" color="primary" onClick={onCheckoutConfirmation}
              disabled={loading} className="confirm-btn">
              Confirm
              {loading && (
                <CircularProgress size={24} className="loader" />
              )}
            </Button>
          </Box>
        </Box>
      }

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className='coupon-modal sub-coupon-modal'
        open={couponModal}
        onClose={() => handleCouponModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={couponModal}>
          <Box className='modal-container'>
            <Typography variant="h5" className="header">Select a Coupon</Typography>
            <Box className="coupon-list">
              {couponList.length > 0 && couponList.map((item, index) =>
                <Box key={index}
                  onClick={() => onCouponSelect(item)}
                  className={item.couponCode === selectedCoupon.couponCode ? 'coupon-selected list-item' : 'list-item'}>
                  <Box className='coupon-dtls'>
                    Code: {item.couponCode}
                    <Box className='coupon-exp'>
                      Expiry: {new Date(item.expiryDate).toDateString()}
                    </Box>
                  </Box>

                  {item.discountType === 'percentage' &&
                    <Box className='coupon-amt'>
                      {item.discountPercentage}%
                    </Box>
                  }
                  {item.discountType !== 'percentage' &&
                    <Box className='coupon-amt'>
                      ${item.discountAmount}
                    </Box>
                  }
                </Box>
              )
              }
            </Box>

            {selectedCoupon.couponCode &&
              <Box className="apply-btn">
                <Button color="primary" onClick={() => handleCouponModal(false)}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={onCouponApplied}>
                  Apply
                </Button>
              </Box>
            }
          </Box>
        </Fade>
      </Modal>

      <Modal
        className='coupon-modal plan-details-modal'
        open={planDetailsModal}
        onClose={() => hidePlanModal()}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={planDetailsModal}>
          <Box className='modal-container'>
            <Button className="close-btn" size="small" onClick={() => hidePlanModal()}>
              <Close></Close>
            </Button>
            <Typography variant="h5" className="header">Plan Details</Typography>
            <Box className="plan-details-wrapper">
              <Box>
                <b>Name:</b> {planDetails.name}
              </Box>
              <Box>
                <b>Exam Types:</b> {planDetails.examTypes?.join(", ", " ")}
              </Box>
              <Box>
                <b>Price:</b> {planDetails.price}
              </Box>
              <Box>
                <b>Duration:</b> {CONSTANTS.durationObj[planDetails.durationInDay] || planDetails.durationInDay}
              </Box>
              <Box>
                <b>Questions Per Day:</b> {planDetails.questionsPerDay}
              </Box>
              {planDetails.description &&
                <Box>
                  <b>Description:</b> <br />
                  <ReactMarkdown>{planDetails.description}</ReactMarkdown>
                </Box>
              }
            </Box>

            <Box className="apply-btn">
              <Button variant="contained" color="primary" onClick={() => hidePlanModal()}>
                Ok
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box >
  );
}
